import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useMemo,
  ReactNode,
} from 'react'
import { BillingScheduleItem } from '@sportninja/common/types/Registration'
import { uiReducer, initialUIState } from './reducer'
import { TeamBillingUIState } from './types'

interface UIContextValue extends TeamBillingUIState {
  openSendInvoiceModal: (billing: BillingScheduleItem) => void
  closeSendInvoiceModal: () => void
  addPdfLoading: (id: string) => void
  removePdfLoading: (id: string) => void
}

const UIContext = createContext<UIContextValue | null>(null)

export function useUIContext() {
  const ctx = useContext(UIContext)
  if (!ctx) {
    throw new Error('useUIContext must be used within UIProvider')
  }
  return ctx
}

export function UIProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(uiReducer, initialUIState)

  const openSendInvoiceModal = useCallback((billing: BillingScheduleItem) => {
    dispatch({ type: 'OPEN_SEND_INVOICE_MODAL', payload: billing })
  }, [])

  const closeSendInvoiceModal = useCallback(() => {
    dispatch({ type: 'CLOSE_SEND_INVOICE_MODAL' })
  }, [])

  const addPdfLoading = useCallback((id: string) => {
    dispatch({ type: 'ADD_PDF_LOADING', payload: id })
  }, [])

  const removePdfLoading = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_PDF_LOADING', payload: id })
  }, [])

  const value = useMemo(
    () => ({
      ...state,
      openSendInvoiceModal,
      closeSendInvoiceModal,
      addPdfLoading,
      removePdfLoading,
    }),
    [
      state,
      openSendInvoiceModal,
      closeSendInvoiceModal,
      addPdfLoading,
      removePdfLoading,
    ]
  )

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>
}
